import { format } from 'date-fns' // eslint-disable-line
import { ptBR } from 'date-fns/locale' // eslint-disable-line
import jsPDF from 'jspdf'

import { ICardItem } from 'src/types/card'

// ----------------------------------------------------------------------
// Exportação da lista de cartões (Nome, Cartão, Saldo) em PDF e Excel/CSV.
// Saldo vem em centavos.

const BRAND: [number, number, number] = [102, 126, 234]
const INK: [number, number, number] = [33, 43, 54]
const MUTED: [number, number, number] = [99, 115, 129]
const GREEN: [number, number, number] = [34, 139, 34]
const RED: [number, number, number] = [200, 50, 50]
const ZEBRA: [number, number, number] = [245, 247, 250]

const MARGIN = 14
const PAGE_BOTTOM = 285

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((v || 0) / 100)

// Número em reais com vírgula decimal, sem símbolo — o Excel pt-BR lê como número.
const numberForSheet = (cents: number) => ((cents || 0) / 100).toFixed(2).replace('.', ',')

const timestamp = () => format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })
const fileStamp = () => format(new Date(), 'yyyy-MM-dd_HHmm')

export type ExportMeta = {
  totalBalance: number
  filterLabel?: string
}

// ----------------------------------------------------------------------

const balanceColor = (balance: number): [number, number, number] => {
  if (balance < 0) return RED
  if (balance > 0) return GREEN
  return MUTED
}

export function exportCardsToPdf(cards: ICardItem[], meta: ExportMeta) {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()

  // Cabeçalho
  doc.setFillColor(...BRAND)
  doc.rect(0, 0, pageWidth, 24, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(15)
  doc.text('Cashless — Cartões', MARGIN, 11)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text(
    `Gerado em ${timestamp()}${meta.filterLabel ? ` · ${meta.filterLabel}` : ''}`,
    MARGIN,
    18,
  )

  // Colunas: Nome | Cartão | Saldo (direita)
  const xName = MARGIN
  const xCard = 105
  const xBalance = pageWidth - MARGIN
  let y = 34

  const drawHeader = () => {
    doc.setFillColor(...INK)
    doc.rect(MARGIN, y - 5, pageWidth - MARGIN * 2, 7, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.text('Nome', xName + 2, y)
    doc.text('Cartão', xCard, y)
    doc.text('Saldo', xBalance - 2, y, { align: 'right' })
    y += 6
  }

  drawHeader()

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)

  cards.forEach((card, i) => {
    if (y > PAGE_BOTTOM) {
      doc.addPage()
      y = 20
      drawHeader()
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
    }

    if (i % 2 === 0) {
      doc.setFillColor(...ZEBRA)
      doc.rect(MARGIN, y - 4, pageWidth - MARGIN * 2, 6, 'F')
    }

    const name = card.name || 'Sem nome'
    const balance = card.balance || 0

    doc.setTextColor(...INK)
    doc.text(String(name).slice(0, 55), xName + 2, y)
    doc.setTextColor(...MUTED)
    doc.text(String(card.card || '—'), xCard, y)
    doc.setTextColor(...balanceColor(balance))
    doc.text(fmtCurrency(balance), xBalance - 2, y, { align: 'right' })

    y += 6
  })

  // Rodapé com totais
  if (y > PAGE_BOTTOM - 10) {
    doc.addPage()
    y = 20
  }
  y += 2
  doc.setDrawColor(...INK)
  doc.setLineWidth(0.3)
  doc.line(MARGIN, y, pageWidth - MARGIN, y)
  y += 6
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.setTextColor(...INK)
  doc.text(`Total de cartões: ${cards.length}`, xName + 2, y)
  doc.setTextColor(...(meta.totalBalance < 0 ? RED : GREEN))
  doc.text(`Saldo total: ${fmtCurrency(meta.totalBalance)}`, xBalance - 2, y, { align: 'right' })

  doc.save(`cartoes_${fileStamp()}.pdf`)
}

// ----------------------------------------------------------------------

export function exportCardsToCsv(cards: ICardItem[], meta: ExportMeta) {
  const escape = (value: string) => `"${String(value).replace(/"/g, '""')}"`
  const sep = ';'

  const rows = [
    ['Nome', 'Cartão', 'Saldo (R$)'].map(escape).join(sep),
    ...cards.map((card) =>
      [
        escape(card.name || 'Sem nome'),
        escape(String(card.card || '')),
        numberForSheet(card.balance || 0),
      ].join(sep),
    ),
    '',
    [escape('Total'), '', numberForSheet(meta.totalBalance)].join(sep),
  ]

  // BOM para o Excel reconhecer o UTF-8 e a acentuação.
  const csv = `\uFEFF${rows.join('\r\n')}`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `cartoes_${fileStamp()}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
